// Mapper to create WebApiMemberDtoAddMemberObjectDto from uploadFields and memberInFile
// Assumes uploadFields is an array of objects with a 'type' property matching MemberFieldTypes
// and memberInFile is an array of CSV values in the same order as uploadFields

import { formatDateString, getDateStringFromDate } from "../Helpers/DateHelpers";
import { WebApiMemberDtoAddMemberObjectDto, WebApiMemberDtoCustomMemberFieldValueDto, WebApiMemberDtoGroupDto, WebApiMemberFieldsDtoMemberUploadFieldDto } from "../Repository/eventbookyapi";
import { CustomField } from "../Types/CustomField";
import { UI_CONTROLS } from "../Types/FieldTypes";
import { GroupType } from "../Types/GroupType";
import { MemberFieldTypes } from "../Types/MemberFieldTypes";
import { UiControlType } from "../Types/UiControlType";
import { mapToMemberStatus } from "./mapGroupToListItem";

// import { WebApiMemberDtoAddMemberObjectDto } from "../../Repository/eventbookyapi";
// Uncomment and adjust the import above to match your DTO location

// Helper: map field type to DTO property name
const memberFieldTypeToDtoKey: Record<MemberFieldTypes, string> = {
    [MemberFieldTypes.UniqueId]: "uniqueId",
    [MemberFieldTypes.FirstName]: "firstName",
    [MemberFieldTypes.LastName]: "lastName",
    [MemberFieldTypes.Email]: "email",
    [MemberFieldTypes.Email2]: "email2",
    [MemberFieldTypes.PhoneNumber]: "phoneNumber",
    [MemberFieldTypes.Address]: "address",
    [MemberFieldTypes.PostalCode]: "postalCode",
    [MemberFieldTypes.PostalArea]: "postalArea",
    [MemberFieldTypes.Title]: "title",
    [MemberFieldTypes.Country]: "country",
    [MemberFieldTypes.Company]: "company",
    [MemberFieldTypes.Extra1]: "extra1",
    [MemberFieldTypes.Extra2]: "extra2",
    [MemberFieldTypes.Extra3]: "extra3",
    [MemberFieldTypes.Group]: "groups",
    [MemberFieldTypes.Status]: "status",
    [MemberFieldTypes.Payment]: "paymentAmountCurrentYear", // Adjust as needed
    [MemberFieldTypes.EmailNotificationPreference]: "emailNotificationPreference",
    [MemberFieldTypes.SortOrder]: "sortOrder",
    [MemberFieldTypes.PersonalNumber]: "personalNumber",
    [MemberFieldTypes.PhoneNumber2]: "phoneNumber2",
    [MemberFieldTypes.AdressRow2]: "adressRow2",
    [MemberFieldTypes.AdressRow3]: "adressRow3",
    [MemberFieldTypes.JoinedDate]: "joinedDate",
    [MemberFieldTypes.WebPage]: "webPage",
    [MemberFieldTypes.Membership]: "membership",
    [MemberFieldTypes.MembershipAddon]: "membershipAddon",
    [MemberFieldTypes.Custom]: "custom", // Custom fields may need special handling
    [MemberFieldTypes.UnSpecified]: "unspecified"
};

// Main mapper function
export function mapUploadFieldsToBulkMemberDto(uploadFields: WebApiMemberFieldsDtoMemberUploadFieldDto[], memberInFile: string[], headers: string[], groups: WebApiMemberDtoGroupDto[]): WebApiMemberDtoAddMemberObjectDto {
    const dto: WebApiMemberDtoAddMemberObjectDto = {};
    const paymentFields = [
        "paymentAmountYearMinusTwo",
        "paymentAmountYearMinusOne",
        "paymentAmountCurrentYear",
        "paymentAmountNextYear"
    ];
    const paymentStartIdx = memberInFile.length - 4;
    // Assign payment fields from the last 4 columns
    paymentFields.forEach((field, i) => {
        const value = trimFromCsv(memberInFile[paymentStartIdx + i]);
        (dto as any)[field] = value === "" ? null : Number(value);
    });

    const customFields: WebApiMemberDtoCustomMemberFieldValueDto[] = [];

    // Helper to map yes/no/ja/nej/1/0 to 'true'/'false' string, returns undefined if not matched
    function mapYesNoToBooleanString(value: string): string | undefined {
        const v = value.trim().toLocaleLowerCase();
        if (v === "yes" || v === "1" || v === "ja") return "true";
        if (v === "no" || v === "0" || v === "nej") return "false";
        return undefined;
    }


    // Assign all other fields except payment fields
    uploadFields.forEach((field, idx) => {
        if (field.fieldType == MemberFieldTypes.Custom) {
            headers.forEach((header, headerIdx) => {
                if (header === field.customFieldName) {
                    const value = trimFromCsv(memberInFile[headerIdx]);

                    if (value) {
                        if (field.uiControl == UiControlType.YesNo) {
                            const boolString = mapYesNoToBooleanString(value);
                            if (boolString !== undefined) {
                                customFields.push({ fieldId: field.id!, fieldValue: boolString });
                            }
                        } else {
                            customFields.push({ fieldId: field.id!, fieldValue: value });
                        }
                    }

                }
            });
        }
    });
    dto.customFieldValues = customFields;

    // Assign all other fields except payment fields
    uploadFields.forEach((field, idx) => {
        const type = field.fieldType;
        const key = memberFieldTypeToDtoKey[type!];
        if (paymentFields.includes(key)) return; // skip payment fields
        let value = trimFromCsv(memberInFile[idx]);
        if (key === "groups") {
            dto.groups = getDefaultGroups(groups);
        } else if (key === "status") {
            dto.status = mapToMemberStatus(value);
        } else if (key === "joinedDate") {
            dto.joinedDate = new Date(value);
        } else if (key === "emailNotificationPreference") {
            dto.emailNotificationPreference = mapYesNoToBooleanString(value) ? 1 : 0;
        } else {
            (dto as any)[key] = value;
        }
    });
    return dto;
}

const trimFromCsv = (value: string) => {
    return value ? value.trim() : ""
}

const getDefaultGroups = (groups: WebApiMemberDtoGroupDto[]) => {
    var result = groups.filter((p: WebApiMemberDtoGroupDto) => p.groupType == GroupType.Default);
    return result.map(p => p.name).join(",")
}
